'use client';
import React from 'react';

import { IRepository, IResourceVersion } from '@/types';
import { ICompareResponse, TComparison } from '@/lib/tabularCompareApi';
import useTabularCompare from '@/hooks/useTabularCompare';

import Tag from '@/ui/Tag';
import { Box, Stack, Row } from '@/ui/primatives';
import * as Icon from '@/ui/Icon';
import * as Styled from './styled';
import { toTimeAgo } from '@/lib/dateTime';
import useOxenSession from '@/hooks/useOxenSession';

const TitleEditor: React.FC<{ title: string; enabled?: boolean; onChange: (title: string) => void }> = ({
  title,
  enabled,
  onChange,
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [newTitle, setNewTitle] = React.useState(title);

  const handleSave = () => {
    onChange(newTitle);
    setIsEditing(false);
  };

  const cancelChanges = () => {
    setNewTitle(title);
    setIsEditing(false);
  };

  return (
    <Styled.TitleEditor>
      {enabled && isEditing ? (
        <input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          autoFocus
          onBlur={handleSave}
          onKeyDown={(e) => e.key === 'Enter' && handleSave()}
        />
      ) : (
        <h5 onClick={() => enabled && setIsEditing(true)}>
          <Row gap={5} alignItems="center">
            {newTitle}
            {enabled && <Icon.Edit className="edit-icon" size="18" />}
          </Row>
        </h5>
      )}
    </Styled.TitleEditor>
  );
};

const DescriptionEditor: React.FC<{ description?: string; enabled?: boolean; onChange: (title: string) => void }> = ({
  description,
  enabled,
  onChange,
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [value, setValue] = React.useState(description);

  const handleSave = () => {
    !!value && onChange(value);
    setIsEditing(false);
  };

  const cancelChanges = () => {
    setValue(description);
    setIsEditing(false);
  };

  if (!enabled && !description) {
    return null;
  }

  return (
    <Styled.DescriptionEditor>
      {enabled && isEditing ? (
        <textarea
          value={value || undefined}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
          onBlur={handleSave}
          onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          cols={80}
          rows={3}
        />
      ) : (
        <div onClick={() => enabled && setIsEditing(true)}>
          <Row gap={5} alignItems="center">
            {enabled ? value || 'Add a description' : value}
            {enabled && <Icon.Edit className="edit-icon" size="18" />}
          </Row>
        </div>
      )}
    </Styled.DescriptionEditor>
  );
};

const CompareResources: React.FC<{ left: IResourceVersion; right: IResourceVersion }> = ({ left, right }) => {
  return (
    <Row gap={5} alignItems="center">
      <b>Comparing: </b>
      <Row gap={5} alignItems="center">
        <Tag small>{left?.path}</Tag>
        <Tag variant="info" small>
          <Icon.Branch height={12} /> {left?.version}
        </Tag>
      </Row>
      <b>to</b>
      <Row gap={5} alignItems="center">
        <Tag small>{right?.path}</Tag>
        <Tag variant="info" small>
          <Icon.Branch height={12} /> {right?.version}
        </Tag>
      </Row>
    </Row>
  );
};

const CompareFileHeading: React.FC<{
  compareResponse: ICompareResponse;
  compare: TComparison;
  repository: IRepository;
  children?: React.ReactNode;
}> = ({ compareResponse, compare: initialCompare, repository, children }) => {
  const { authorization } = useOxenSession();

  const { loading, compare, updateCompare, status, error } = useTabularCompare({
    repository,
    compareNumber: initialCompare.compare_number,
    initialData: compareResponse,
  });

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!compare) {
    return <div>Failed to load compare: {JSON.stringify(error)}</div>;
  }

  const {
    definition: { data_frames: dataFrames, title, description },
  } = compare;

  const canEdit = authorization.forRepository(repository).canEdit();

  return (
    <Box style={{ padding: 20 }}>
      <Stack gap={20}>
        <Stack gap={10}>
          <Row gap={20} justifyContent="space-between">
            <TitleEditor enabled={canEdit} title={title || `Untitled`} onChange={(title) => updateCompare({ title })} />
            <div>
              created by: {compare.created_by?.username}, {toTimeAgo(compare.inserted_at)}
            </div>
          </Row>
          <DescriptionEditor
            enabled={canEdit}
            description={description}
            onChange={(description) => updateCompare({ description })}
          />
        </Stack>
        <Row gap={20} justifyContent="space-between" alignItems="center">
          <CompareResources left={dataFrames.left} right={dataFrames.right} />
          {children}
        </Row>
      </Stack>
    </Box>
  );
};

export default CompareFileHeading;
